import { FC, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { ApprovedUrl } from "@models/approvedUrl.model";
import OrganizationPicker from "@components/OrganizationPicker/OrganizationPicker";

interface Props {
  show: boolean;
  approvedUrl: ApprovedUrl | null;
  currentOrganizationId: number;
  isSaving: boolean;
  onClose: () => void;
  onMoveConfirmed: (destinationOrganizationId: number) => Promise<void>;
}

/**
 * Reassigns an approved URL to another organization — the correction path when the
 * OP->NP migration resolved the wrong owner. Only organizations the user can edit are
 * offered, because the destination is authorized as well as the source.
 */
const MoveApprovedUrlModal: FC<Props> = ({
  show,
  approvedUrl,
  currentOrganizationId,
  isSaving,
  onClose,
  onMoveConfirmed,
}) => {
  const [destinationId, setDestinationId] = useState<number | null>(null);

  useEffect(() => {
    if (show) setDestinationId(null);
  }, [show]);

  if (!approvedUrl) return null;

  const unchanged = destinationId === null || destinationId === currentOrganizationId;

  return (
    <Modal show={show} onHide={onClose} centered backdrop={isSaving ? "static" : true}>
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title as="h5">Move approved URL</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-1 fw-semibold">{approvedUrl.title}</p>
        <p className="text-muted small text-break">{approvedUrl.url}</p>
        <div className="mt-3">
          <OrganizationPicker
            value={destinationId}
            onChange={setDestinationId}
            label="Move to organization"
            disabled={isSaving}
          />
        </div>
        {destinationId === currentOrganizationId && (
          <p className="text-muted small mt-2 mb-0">
            That is where the URL already lives.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={isSaving || unchanged}
          onClick={() => destinationId != null && onMoveConfirmed(destinationId)}
        >
          {isSaving ? "Moving…" : "Move URL"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MoveApprovedUrlModal;
